import React, { useState } from "react";
import GenericModal from "./GenericModal";
import useModalStore from "../../stores/modalStore";
import useUserStore from "../../stores/userStore";
import getFirebaseAuthToken from "../../services/fireBaseAuthenticationService";
import axios from "axios";
import { BACKEND_CALL_TIMEOUT, BACKEND_URL } from "../../utils/constants";
import { loggedAlert } from "../../services/eventService";
import LoadingSpinner from "../../LoadingSpinner";

const DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const POSITIONS = [
    { value: "groomer", label: "Groomer" },
    { value: "receptionist", label: "Receptionist" },
    { value: "manager", label: "Manager" },
    { value: "Owner", label: "Owner" },
];

const initialForm = {
    user_id: "",
    first_name: "",
    preferred_first_name: "",
    last_name: "",
    email: "",
    phone: "",
    birth_date: "",
    start_date: new Date().toISOString().split("T")[0],
    position: "groomer",
    password: "",
    pay_type: "hourly",
    base_rate: "24",
    merit_rate: "0",
    commission_percentage: "",
    daysmart_id_loc1: "",
    daysmart_id_loc2: "",
    schedule: {
        sunday: "0",
        monday: "0",
        tuesday: "0",
        wednesday: "0",
        thursday: "0",
        friday: "0",
        saturday: "0",
    },
};

const AddEmployeeModal = () => {
    const [form, setForm] = useState({ ...initialForm, schedule: { ...initialForm.schedule } });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const closeModal = useModalStore((state) => state.closeModal);
    const { currentUser } = useUserStore();

    const updateField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const updateScheduleDay = (day, value) => {
        setForm((prev) => ({
            ...prev,
            schedule: { ...prev.schedule, [day]: value },
        }));
    };

    const handleSubmit = async () => {
        if (!form.user_id || !form.first_name || !form.last_name || !form.password || !form.start_date) {
            loggedAlert("Please fill in all required fields: User ID, First Name, Last Name, Password, Start Date.", currentUser?.user_id);
            return;
        }

        setIsSubmitting(true);
        try {
            const token = await getFirebaseAuthToken();

            const payload = {
                user_id: parseInt(form.user_id, 10),
                first_name: form.first_name,
                preferred_first_name: form.preferred_first_name || null,
                last_name: form.last_name,
                email: form.email || null,
                phone: form.phone || null,
                birth_date: form.birth_date || null,
                start_date: form.start_date,
                position: form.position,
                password: form.password,
                pay_type: form.pay_type,
                base_rate: parseFloat(form.base_rate) || 0,
                merit_rate: parseFloat(form.merit_rate) || 0,
                commission_percentage: form.pay_type === "commission" ? parseFloat(form.commission_percentage) || 0 : null,
                daysmart_id_loc1: form.daysmart_id_loc1 || null,
                daysmart_id_loc2: form.daysmart_id_loc2 || null,
                schedule: Object.fromEntries(
                    DAYS.map((d) => [d, parseFloat(form.schedule[d]) || 0])
                ),
            };

            const response = await axios.post(`${BACKEND_URL}/employees`, payload, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: BACKEND_CALL_TIMEOUT,
            });

            if (response.status === 201) {
                alert(`✅ ${response.data.message}`);
                setForm({ ...initialForm, schedule: { ...initialForm.schedule } });
                closeModal("addEmployeeModal");
                // Reload page to refresh employee list from cache-busted backend
                window.location.reload();
            }
        } catch (error) {
            const msg =
                error?.response?.data?.error ||
                error?.message ||
                "Unknown error occurred";
            loggedAlert(`Error creating employee: ${msg}`, currentUser?.user_id);
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputClass =
        "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none";
    const labelClass = "block text-xs font-semibold text-gray-600 mb-1";

    return (
        <GenericModal
            name="addEmployeeModal"
            title="Add New Employee"
            hideButton
            modalMaxWidth="max-w-3xl"
            stickyFooter={
                <div className="flex justify-end gap-3">
                    <button
                        onClick={() => closeModal("addEmployeeModal")}
                        className="!px-4 !py-2 !text-sm !font-medium !text-gray-600 !bg-white !border !border-gray-300 !rounded-lg hover:!bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="!px-5 !py-2 !text-sm !font-medium !text-white !bg-indigo-600 !rounded-lg hover:!bg-indigo-700 disabled:!opacity-50 !flex !items-center !gap-2"
                    >
                        {isSubmitting ? <><LoadingSpinner size="sm" /> Creating...</> : "Create Employee"}
                    </button>
                </div>
            }
        >
            <div className="p-6 space-y-6">
                {/* Identity */}
                <section>
                    <h3 className="text-sm font-bold text-gray-800 mb-3">Identity</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>
                                User ID <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                value={form.user_id}
                                onChange={(e) => updateField("user_id", e.target.value)}
                                className={inputClass}
                                placeholder="e.g. 132"
                            />
                            <p className="text-[10px] text-gray-400 mt-1">
                                This should come from isolved
                            </p>
                        </div>
                        <div>
                            <label className={labelClass}>
                                Password <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={form.password}
                                onChange={(e) => updateField("password", e.target.value)}
                                className={inputClass}
                                placeholder="Temporary password"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>
                                First Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={form.first_name}
                                onChange={(e) => updateField("first_name", e.target.value)}
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Preferred First Name</label>
                            <input
                                type="text"
                                value={form.preferred_first_name}
                                onChange={(e) => updateField("preferred_first_name", e.target.value)}
                                className={inputClass}
                                placeholder="e.g. Gabi"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>
                                Last Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={form.last_name}
                                onChange={(e) => updateField("last_name", e.target.value)}
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Email</label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={(e) => updateField("email", e.target.value)}
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Phone</label>
                            <input
                                type="tel"
                                value={form.phone}
                                onChange={(e) => updateField("phone", e.target.value)}
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Birth Date</label>
                            <input
                                type="date"
                                value={form.birth_date}
                                onChange={(e) => updateField("birth_date", e.target.value)}
                                className={inputClass}
                            />
                        </div>
                    </div>
                </section>

                {/* Role & Start Date */}
                <section>
                    <h3 className="text-sm font-bold text-gray-800 mb-3">Role & Start Date</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>
                                Position <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={form.position}
                                onChange={(e) => updateField("position", e.target.value)}
                                className={inputClass}
                            >
                                {POSITIONS.map((p) => (
                                    <option key={p.value} value={p.value}>
                                        {p.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={labelClass}>
                                Start Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                value={form.start_date}
                                onChange={(e) => updateField("start_date", e.target.value)}
                                className={inputClass}
                            />
                        </div>
                    </div>
                </section>

                {/* Pay Structure */}
                <section>
                    <h3 className="text-sm font-bold text-gray-800 mb-3">Pay Structure</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>Pay Type</label>
                            <select
                                value={form.pay_type}
                                onChange={(e) => updateField("pay_type", e.target.value)}
                                className={inputClass}
                            >
                                <option value="hourly">Hourly</option>
                                <option value="commission">Commission</option>
                            </select>
                        </div>

                        {form.pay_type === "hourly" && (
                            <>
                                <div>
                                    <label className={labelClass}>Base Rate ($/hr)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={form.base_rate}
                                        onChange={(e) => updateField("base_rate", e.target.value)}
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Merit Rate ($/hr)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={form.merit_rate}
                                        onChange={(e) => updateField("merit_rate", e.target.value)}
                                        className={inputClass}
                                    />
                                </div>
                            </>
                        )}

                        {form.pay_type === "commission" && (
                            <div>
                                <label className={labelClass}>Commission Percentage (%)</label>
                                <input
                                    type="number"
                                    step="1"
                                    value={form.commission_percentage}
                                    onChange={(e) => updateField("commission_percentage", e.target.value)}
                                    className={inputClass}
                                    placeholder="e.g. 55"
                                />
                            </div>
                        )}
                    </div>
                </section>

                {/* Schedule */}
                <section>
                    <h3 className="text-sm font-bold text-gray-800 mb-3">
                        Weekly Schedule
                        <span className="ml-2 text-[10px] font-normal text-gray-400">
                            Typical: 7.5 or 9.5 hrs/day
                        </span>
                    </h3>
                    <div className="grid grid-cols-7 gap-2">
                        {DAYS.map((day, i) => (
                            <div key={day} className="text-center">
                                <label className="block text-[10px] font-semibold text-gray-500 mb-1">
                                    {DAY_LABELS[i]}
                                </label>
                                <input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    max="24"
                                    value={form.schedule[day]}
                                    onChange={(e) => updateScheduleDay(day, e.target.value)}
                                    className="w-full px-1 py-1.5 text-sm text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                                />
                            </div>
                        ))}
                    </div>
                </section>

                {/* DaySmart IDs */}
                <section>
                    <h3 className="text-sm font-bold text-gray-800 mb-3">DaySmart IDs</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>Hopkins (Location 1)</label>
                            <input
                                type="text"
                                value={form.daysmart_id_loc1}
                                onChange={(e) => updateField("daysmart_id_loc1", e.target.value)}
                                className={inputClass}
                                placeholder="e.g. 1015"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Inver Grove (Location 2)</label>
                            <input
                                type="text"
                                value={form.daysmart_id_loc2}
                                onChange={(e) => updateField("daysmart_id_loc2", e.target.value)}
                                className={inputClass}
                                placeholder="e.g. 1025"
                            />
                        </div>
                    </div>
                </section>
            </div>
        </GenericModal>
    );
};

export default AddEmployeeModal;
